import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import Toast from '@/components/base/Toast';
import { PriorityBadge, StatusBadge } from '@/components/feature/StatusBadge';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { systemSettings } from '@/mocks/settings';
import { fetchItems } from '@/services/itemsService';
import { setDriverAvailability } from '@/services/driversService';
import {
  acceptOrder,
  adoptReview,
  assignDeliveryDriver,
  fetchOrderById,
  markReadyForDelivery,
  receiveOrder,
  rejectOrder,
  startOrder,
} from '@/services/ordersService';
import {
  DELIVERY_DRIVER_METHODS,
  PICKUP_DRIVER_METHODS,
  type Driver,
  type LaundryItem,
  type OrderView,
  type ReviewLine,
} from '@/types';
import AssignDriverModal from './components/AssignDriverModal';
import EscalationAlert from './components/EscalationAlert';
import OrderAmountSummary from './components/OrderAmountSummary';
import OrderItemsList from './components/OrderItemsList';
import ReviewContents from './components/ReviewContents';

type ConfirmAction = 'accept' | 'reject' | 'receive' | 'ready';
type ToastState = { message: string; tone: 'success' | 'error' };
interface ActionArea {
  buttons: ReactNode;
  note?: ReactNode;
}

function InfoNote({ icon, text }: { icon: string; text: string }) {
  return (
    <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-600">
      <i className={`${icon} mt-0.5 text-base text-foreground-400`} aria-hidden="true" />
      {text}
    </p>
  );
}

export default function OrderDetailPage() {
  const { t, i18n } = useTranslation();
  const { orderId = '' } = useParams();
  const [searchParams] = useSearchParams();

  const [order, setOrder] = useState<OrderView | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [items, setItems] = useState<LaundryItem[]>([]);

  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(false);
    setNotFound(false);
    try {
      const data = await fetchOrderById(orderId);
      if (data) setOrder(data);
      else setNotFound(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  useEffect(() => {
    fetchItems()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  const notify = (message: string, tone: ToastState['tone'] = 'success') => setToast({ message, tone });

  const refresh = async () => {
    const data = await fetchOrderById(orderId).catch(() => null);
    if (data) setOrder(data);
  };

  const runAction = async (fn: () => Promise<OrderView>, successKey: string) => {
    setConfirmLoading(true);
    try {
      const updated = await fn();
      setOrder(updated);
      setConfirm(null);
      notify(t(successKey));
    } catch (err) {
      setConfirm(null);
      if (err instanceof Error && err.message === 'order-state-changed') {
        await refresh();
        notify(t('orders.stateChanged'), 'error');
      } else {
        notify(t('orders.errorAction'), 'error');
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleAdopt = async (lines: ReviewLine[]) => {
    if (!order) return;
    const updated = await adoptReview(order.id, { lines });
    setOrder(updated);
    notify(t('orders.toast.reviewAdopted'));
  };

  const handleAssign = async (driver: Driver) => {
    if (!order) return;
    const updated = await assignDeliveryDriver(order.id, driver.id, driver.fullName);
    await setDriverAvailability(driver.id, 'busy');
    setOrder(updated);
    notify(t('orders.toast.assigned'));
  };

  // Return to the orders list on the SAME tab the user came from (?tab=active …).
  const returnTab = searchParams.get('tab');
  const backTo =
    returnTab && returnTab !== 'new' ? `/dashboard/orders?tab=${returnTab}` : '/dashboard/orders';

  const backLink = (
    <Link
      to={backTo}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 transition-colors hover:text-foreground-900"
    >
      <i className="ri-arrow-left-line text-lg rtl:rotate-180" aria-hidden="true" />
      {t('orders.backToList')}
    </Link>
  );

  if (loading) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card className="space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-24 w-full" />
        </Card>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <EmptyState
            icon="ri-file-unknow-line"
            title={t('orders.notFound')}
            action={
              <Link
                to="/dashboard/orders"
                className="inline-flex h-11 items-center rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                {t('orders.notFoundBack')}
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <ErrorState
            title={t('orders.errorDetail')}
            description={t('common.errorText')}
            retryLabel={t('common.retry')}
            onRetry={loadOrder}
          />
        </Card>
      </div>
    );
  }

  const needsPickup = PICKUP_DRIVER_METHODS.includes(order.fulfillment);
  const needsDelivery = DELIVERY_DRIVER_METHODS.includes(order.fulfillment);
  const itemsCount = order.items.reduce((sum, item) => sum + item.quantity, 0);

  const renderActions = (): ActionArea => {
    switch (order.status) {
      case 'new':
        return {
          buttons: (
            <>
              <Button
                variant="primary"
                size="md"
                fullWidth
                icon="ri-check-line"
                onClick={() => setConfirm('accept')}
              >
                {t('orders.actions.accept')}
              </Button>
              <Button
                variant="danger-outline"
                size="md"
                fullWidth
                icon="ri-close-line"
                onClick={() => setConfirm('reject')}
              >
                {t('orders.actions.reject')}
              </Button>
            </>
          ),
        };
      case 'accepted':
        return {
          buttons: (
            <Button
              variant="primary"
              size="md"
              fullWidth
              icon="ri-inbox-archive-line"
              onClick={() => setConfirm('receive')}
            >
              {t('orders.actions.receive')}
            </Button>
          ),
          note: (
            <div className="space-y-1.5">
              <InfoNote
                icon={needsPickup ? 'ri-e-bike-2-line' : 'ri-store-2-line'}
                text={t(needsPickup ? 'orders.accept.pickupNote' : 'orders.accept.dropoffNote')}
              />
              <InfoNote icon="ri-timer-flash-line" text={t('orders.duration.startsNote')} />
            </div>
          ),
        };
      case 'under_review':
        if (order.reviewAdopted) {
          return {
            buttons: (
              <Button
                variant="primary"
                size="md"
                fullWidth
                icon="ri-play-circle-line"
                onClick={() => runAction(() => startOrder(order.id), 'orders.toast.started')}
              >
                {t('orders.actions.startProgress')}
              </Button>
            ),
            note: <InfoNote icon="ri-check-double-line" text={t('orders.review.adoptedNote')} />,
          };
        }
        return {
          buttons: null,
          note: <InfoNote icon="ri-arrow-down-line" text={t('orders.review.note')} />,
        };
      case 'in_progress':
        return {
          buttons: (
            <Button
              variant="primary"
              size="md"
              fullWidth
              icon="ri-truck-line"
              onClick={() => setConfirm('ready')}
            >
              {t('orders.actions.ready')}
            </Button>
          ),
        };
      case 'ready_for_delivery':
        return {
          buttons: null,
          note: (
            <InfoNote
              icon={needsDelivery ? 'ri-e-bike-2-line' : 'ri-store-2-line'}
              text={t(needsDelivery ? 'orders.ready.deliveryNote' : 'orders.ready.pickupNote')}
            />
          ),
        };
      case 'completed':
        return { buttons: null, note: <InfoNote icon="ri-checkbox-circle-line" text={t('orders.completed.note')} /> };
      default:
        return { buttons: null };
    }
  };

  const actions = renderActions();

  return (
    <div className="space-y-5">
      {backLink}

      <Card padded={false}>
        <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4 md:px-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="lb-ltr-field text-lg font-bold text-foreground-950">{order.id}</span>
              <StatusBadge status={order.status} />
              <PriorityBadge priority={order.priority} />
            </div>
            <p className="mt-1.5 text-sm text-foreground-600">{order.customerName}</p>
            <p className="mt-0.5 text-xs text-foreground-400">
              {t('orders.detail.createdAt')}: {formatDateTime(order.createdAt, i18n.language)}
            </p>
          </div>
          <div className="text-end">
            <p className="text-xs text-foreground-500">{t('orders.detail.total')}</p>
            <p className="text-xl font-bold text-foreground-950">
              {formatCurrency(order.total, systemSettings.currency)}
            </p>
          </div>
        </div>
      </Card>

      {order.needsManualAssignment ? <EscalationAlert onAssign={() => setAssignOpen(true)} /> : null}

      {order.status === 'under_review' && !order.reviewAdopted ? (
        <ReviewContents
          key={order.id}
          order={order}
          items={items}
          currency={systemSettings.currency}
          onAdopt={handleAdopt}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <h2 className="mb-4 text-base font-semibold text-foreground-950">{t('orders.detail.customer')}</h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-foreground-500">{t('orders.detail.customerName')}</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{order.customerName}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('orders.detail.phone')}</dt>
                <dd className="mt-1 flex items-center gap-2">
                  <span className="lb-ltr-field text-sm font-medium text-foreground-900">{order.customerPhone}</span>
                  <a
                    href={`tel:${order.customerPhone}`}
                    className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-phone-line text-sm" aria-hidden="true" />
                    {t('orders.detail.call')}
                  </a>
                </dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h2 className="mb-4 text-base font-semibold text-foreground-950">{t('orders.detail.orderInfo')}</h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-foreground-500">{t('orders.detail.fulfillment')}</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">
                  {t(`fulfillment.${order.fulfillment}`)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('orders.detail.priority')}</dt>
                <dd className="mt-1">
                  <PriorityBadge priority={order.priority} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('orders.detail.status')}</dt>
                <dd className="mt-1">
                  <StatusBadge status={order.status} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('orders.detail.driver')}</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{order.assignedDriver ?? '—'}</dd>
              </div>
            </dl>
          </Card>

          {order.smartBox ? (
            <Card>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-archive-2-line text-xl" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-foreground-950">{t('orders.smartbox.title')}</h2>
                    <span className="rounded-full border border-accent-200 bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-800">
                      {t(`orders.smartbox.stage.${order.smartBox.stage}`)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-700">{order.smartBox.description}</p>
                  <p className="mt-1.5 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                    <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
                    {t('orders.smartbox.note')}
                  </p>
                </div>
              </div>
            </Card>
          ) : null}

          {order.status === 'cancelled' ? (
            <Card>
              <h2 className="mb-4 text-base font-semibold text-foreground-950">{t('orders.tab.cancelled')}</h2>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs text-foreground-500">{t('orders.cancelled.reason')}</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">
                    {order.cancelReason ? t(`orders.cancelReason.${order.cancelReason}`) : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">{t('orders.cancelled.refund')}</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">
                    {order.refundStatus ? t(`orders.refund.${order.refundStatus}`) : t('orders.refund.none')}
                  </dd>
                </div>
              </dl>
            </Card>
          ) : null}

          <Card padded={false} className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-4 md:px-5">
              <h2 className="text-base font-semibold text-foreground-950">{t('orders.detail.items')}</h2>
              <span className="text-xs text-foreground-500">
                {t('orders.detail.itemsCount')}: {itemsCount}
              </span>
            </div>
            <OrderItemsList items={order.items} />
          </Card>

          <OrderAmountSummary order={order} items={items} currency={systemSettings.currency} />
        </div>

        <div className="space-y-5 lg:col-span-1">
          {actions.buttons || actions.note ? (
            <Card>
              <h2 className="mb-4 text-base font-semibold text-foreground-950">{t('orders.actions.title')}</h2>
              <div className="space-y-3">
                {actions.buttons ? <div className="flex flex-col gap-2.5">{actions.buttons}</div> : null}
                {actions.note}
              </div>
            </Card>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirm === 'accept'}
        tone="primary"
        icon="ri-check-line"
        title={t('orders.accept.confirm.title')}
        text={t('orders.accept.confirm.text')}
        confirmLabel={t('orders.accept.confirm.confirm')}
        cancelLabel={t('orders.confirm.cancel')}
        loading={confirmLoading}
        onConfirm={() => runAction(() => acceptOrder(order.id), 'orders.toast.accepted')}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      <ConfirmDialog
        open={confirm === 'reject'}
        tone="danger"
        icon="ri-close-line"
        title={t('orders.reject.confirm.title')}
        text={t('orders.reject.confirm.text')}
        confirmLabel={t('orders.reject.confirm.confirm')}
        cancelLabel={t('orders.confirm.cancel')}
        loading={confirmLoading}
        onConfirm={() => runAction(() => rejectOrder(order.id), 'orders.toast.rejected')}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      <ConfirmDialog
        open={confirm === 'receive'}
        tone="primary"
        icon="ri-inbox-archive-line"
        title={t('orders.receive.confirm.title')}
        text={t('orders.receive.confirm.text')}
        confirmLabel={t('orders.receive.confirm.confirm')}
        cancelLabel={t('orders.confirm.cancel')}
        loading={confirmLoading}
        onConfirm={() => runAction(() => receiveOrder(order.id), 'orders.toast.received')}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      <ConfirmDialog
        open={confirm === 'ready'}
        tone="primary"
        icon="ri-truck-line"
        title={t('orders.ready.confirm.title')}
        text={t('orders.ready.confirm.text')}
        confirmLabel={t('orders.ready.confirm.confirm')}
        cancelLabel={t('orders.confirm.cancel')}
        loading={confirmLoading}
        onConfirm={() => runAction(() => markReadyForDelivery(order.id), 'orders.toast.ready')}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      <AssignDriverModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onAssign={handleAssign}
      />

      {toast ? <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} /> : null}
    </div>
  );
}